"use client";

import { useState } from "react";
import { useV1Groups, flatGroups, type GroupListSort } from "@/lib/hooks/useGroups";
import { useDebounced } from "@/lib/hooks/useDebounced";
import { useInfiniteScrollRef } from "@/lib/hooks/useInfiniteScrollRef";
import { GroupCard } from "./GroupCard";
import { Input } from "./ui/Input";
import { COVER_ASPECT_CLASS } from "@/lib/coverAspect";

/**
 * The sorts are the SERVER'S (§1.4) and `GroupListSort` is read out of the
 * generated document, so this list cannot offer one the route does not have.
 * The old `?sort=` was a free string the backend had to accept permissively
 * (contract bug C1) precisely because this component could send anything.
 */
const SORT_OPTIONS: { value: GroupListSort; label: string }[] = [
  /**
   * The default is `favourite`, not `recent_activity` (OSO-676): the
   * reporter's rule for the group list is "favourites on top, the rest by the
   * latest post", and that is exactly what the server's `favourite` sort does
   * now — one activity clock across both halves, the flag only pins.
   *
   * WHAT THIS DELIBERATELY DROPS, by owner decision on OSO-676: the
   * public-groups-first pinning `recent_activity` has. On the default view a
   * signed-out visitor — or a member with no favourites — gets pure
   * latest-post ordering, so private-group shells interleave with public
   * groups, and at the `-infinity` tail dormant public groups and dormant
   * private shells mix. That is accepted, not an accident.
   */
  { value: "favourite", label: "Aktive" },
  { value: "largest", label: "Største" },
  { value: "newest", label: "Nyeste" },
];

export function GroupDiscovery({ location }: { location?: string } = {}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<GroupListSort>("favourite");
  const q = useDebounced(search.trim(), 250);
  const query = useV1Groups({ q: q || undefined, sort, location });
  const groups = flatGroups(query.data);
  const isLoading = query.isLoading;
  const sentinelRef = useInfiniteScrollRef(
    query.hasNextPage,
    query.isFetchingNextPage,
    query.fetchNextPage,
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle-foreground pointer-events-none"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg efter grupper..."
            className="w-full border border-border bg-surface text-foreground rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <div className="flex shrink-0 rounded-lg border border-border overflow-hidden">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSort(opt.value)}
              className={`px-3 py-2 text-xs font-medium transition-colors ${
                sort === opt.value
                  ? "bg-indigo-600 text-white"
                  : "bg-surface text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface rounded-xl border border-border overflow-hidden animate-pulse"
            >
              {/* The same ratio the real card's cover wears (OSO-632): a
                  skeleton at the old fixed `h-32` was a different height from
                  the `GroupCard` that replaces it, so the grid jumped on load
                  and dragged the infinite-scroll sentinel with it. */}
              <div className={`w-full ${COVER_ASPECT_CLASS} bg-muted`} />
              <div className="p-3">
                <div className="h-3 bg-muted rounded w-2/3" />
                <div className="h-2 bg-muted rounded w-1/3 mt-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {groups.map((group) => (
          <GroupCard key={group.id} group={group} />
        ))}
      </div>

      {/* CURSOR PAGING, not `?page=` (§1.3/D4). The old component asked for
          page one and stopped there — a "load more" was impossible because the
          response carried a page NUMBER and no way to ask for the next one
          under the same filters.

          THE SENTINEL, not the button that stood here (OSO-573). Every other
          paged list in this app — the home feed, a group's feed, the
          notifications page, the moderation queue — loads the next page when
          you reach the bottom, so a grid that stopped and waited for a click
          read as "that is all the groups there are". `useInfiniteScrollRef` is
          the shared observer the rest of them use. */}
      <div ref={sentinelRef} className="h-8" />
      {query.isFetchingNextPage && (
        <p className="text-center text-subtle-foreground text-sm py-2">
          Indlæser flere grupper…
        </p>
      )}

      {groups.length === 0 && !isLoading && (
        <div className="text-center text-subtle-foreground text-sm py-12">
          Ingen grupper fundet
        </div>
      )}
    </div>
  );
}
